import React from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { FormattedMessage, useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const Wrapper = styled.div`
  min-width: 260px;
  min-height: 180px;
  position: absolute;
  bottom: 100%;
  left: 50%;
  background: #fff;
  padding: 15px 20px;
  border-radius: 3px;
  color: #5b5553;
  box-shadow: -4px 2px 10px 0 rgba(0, 0, 0, 0.11);
  z-index: 10;
  transform: translate(-50%, -25px);

  &::before {
    content: '';
    position: absolute;
    top: calc(100% - 1px);
    left: 50%;
    width: 0;
    height: 0;
    box-sizing: border-box;
    border: 15px solid black;
    border-color: transparent transparent #fff #fff;
    transform: translate(-75%, -50%) rotate(-45deg);
    box-shadow: -4px 4px 4px 0 rgba(0, 0, 0, 0.05);
  }

  @media screen and (min-width: ${(props) => props.theme.medium}) {
    left: 100%;
    top: 50%;
    transform: translate(25px, -50%);

    &::before {
      top: 50%;
      left: 0;
      transform: translate(-50%, -50%) rotate(45deg);
    }
  }

  ul {
    list-style-type: none;
    padding: 5px 0px 0px 0px;
    margin: 0px;
  }

  li {
    display: flex;
    align-items: center;
  }
`;

const SharedIconStyles = css`
  width: 14px !important;
  height: 14px;
  padding-right: 10px;
  margin-top: 2px;
  box-sizing: content-box;
`;

const IconCheck = styled(FontAwesomeIcon)`
  color: green;
  ${SharedIconStyles}
`;

const IconTimes = styled(FontAwesomeIcon)`
  color: #a94442;
  ${SharedIconStyles}
`;

interface PasswordRequirementsProps {
  password: string;
}

const PasswordRequirements = ({ password }: PasswordRequirementsProps) => {
  const intl = useIntl();
  const passwordLength = 8;

  const checkLength = (num: number) => !!(password.length >= num);
  const checkSmallLetter = () => !!/^(?=.*[a-z]).{1,}$/.test(password);
  const checkCapitalLetter = () => !!/^(?=.*[A-Z]).{1,}$/.test(password);
  const checkDigit = () => !!/^(?=.*\d).{1,}$/.test(password);
  const checkSpecialCharacter = () =>
    !!/^(?=.*[^A-Za-z0-9]).{1,}$/.test(password);
  const checkPasswordRequirements = (fulfilled: boolean) =>
    fulfilled ? <IconCheck icon="check" /> : <IconTimes icon="times" />;

  return (
    <Wrapper>
      {/* shows the user the rules for the password and updates every time the user types so that the user can see
       which of the rules are alreay checked. I want to use redux for that but for now I just used prop types */}
      <FormattedMessage
        id="containers.CustomerLogin.SetPassword.Form.PasswordRequirements.message"
        defaultMessage="The password must at least contain:"
      />
      <ul>
        <li>
          {checkPasswordRequirements(checkDigit())}
          {intl.formatMessage({
            id:
              'containers.CustomerLogin.SetPassword.Form.PasswordRequirements.checkDigit',
            defaultMessage: 'one number',
          })}{' '}
          (1,2,3)
        </li>
        <li>
          {checkPasswordRequirements(checkSmallLetter())}
          {intl.formatMessage({
            id:
              'containers.CustomerLogin.SetPassword.Form.PasswordRequirements.checkSmallLetter',
            defaultMessage: 'one small letter',
          })}{' '}
          (u,a,i)
        </li>
        <li>
          {checkPasswordRequirements(checkCapitalLetter())}
          {intl.formatMessage({
            id:
              'containers.CustomerLogin.SetPassword.Form.PasswordRequirements.checkCapitalLetter',
            defaultMessage: 'one capital letter',
          })}{' '}
          (U,A,I)
        </li>
        <li>
          {checkPasswordRequirements(checkSpecialCharacter())}
          {intl.formatMessage({
            id:
              'containers.CustomerLogin.SetPassword.Form.PasswordRequirements.checkSpecialCharacter',
            defaultMessage: 'one special character',
          })}{' '}
          (#,?,+)
        </li>
        <li>
          {checkPasswordRequirements(checkLength(passwordLength))}
          {passwordLength.toString()}{' '}
          {intl.formatMessage({
            id:
              'containers.CustomerLogin.SetPassword.Form.PasswordRequirements.checkLength',
            defaultMessage: 'characters',
          })}
        </li>
      </ul>
    </Wrapper>
  );
};

PasswordRequirements.propTypes = {
  password: PropTypes.string.isRequired,
};

export { PasswordRequirements };
